import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '@mui/material';

const tabsStyles = {
  borderBottom: 1,
  borderBottomColor: 'graphic.light',
  display: 'inline-flex',
  gridColumnGap: 24,
  mx: 2,
  zIndex: 2,
};

const tabsMobileStyles = {
  backgroundColor: 'surface.white',
  bottom: 0,
  boxShadow: 0,
  height: 56,
  left: 0,
  margin: 0,
  position: 'fixed',
  right: 0,
  width: '100%',
  zIndex: 10,
};

const FilterTabs = ({ activeTab, onChange, isLoading, tabs }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  // prettier-ignore
  const tabsContainerStyles = lgUp 
    ? { ...tabsStyles }
    : { ...tabsStyles, ...tabsMobileStyles };

  if (lgUp && isLoading) return <TabsSkeleton />;

  return (
    <Tabs onChange={onChange} sx={tabsContainerStyles} value={activeTab}>
      {tabs.map(({ id, name, shortName, count }) => {
        const isActive = id === activeTab;
        const label = (
          <TabLabel count={count} isActive={isActive} name={name} shortName={shortName} />
        );
        return <Tab key={id} label={label} value={id} />;
      })}
    </Tabs>
  );
};

FilterTabs.propTypes = {
  activeTab: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

FilterTabs.defaultProps = {
  activeTab: '',
};

export default FilterTabs;

const TabLabel = ({ name, shortName, count, isActive }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const labelColor = isActive ? 'primary.dark' : 'text.link';

  return (
    <Box sx={{ display: 'flex' }}>
      <Typography
        sx={{
          color: labelColor,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          mr: { xs: 0, lg: 1 },
        }}
        variant="tab"
      >
        {lgUp ? name : shortName}
      </Typography>
      {lgUp && (
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'primary.light',
            display: 'flex',
            height: 20,
            justifyContent: 'center',
            width: 22,
          }}
        >
          <Typography variant="caption">{count}</Typography>
        </Box>
      )}
    </Box>
  );
};
TabLabel.propTypes = {
  count: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  shortName: PropTypes.string.isRequired,
};

const TabsSkeleton = () => (
  <Box display="flex" pb={3} pl={3} pt={2}>
    <Box bgcolor="surface.white" mr={3} width={160}>
      <Box alignItems="center" display="flex" height="40px" justifyContent="space-between" px={1}>
        <Skeleton variant="text" width="55%" />
        <Skeleton height="20px" sx={{ borderRadius: '5px' }} variant="rectangular" width="60px" />
      </Box>
    </Box>
    <Box bgcolor="surface.white" mr={3} width={160}>
      <Box alignItems="center" display="flex" height="40px" justifyContent="space-between" px={1}>
        <Skeleton variant="text" width="55%" />
        <Skeleton height="20px" sx={{ borderRadius: '5px' }} variant="rectangular" width="60px" />
      </Box>
    </Box>
  </Box>
);
